import {
  Table as MuiTable,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Typography,
} from '@mui/material';
import './table.scss';
import { useEffect, useState, useRef } from 'react';
import { IconDelete, IconEdit } from '../../../icons/Icon';
import MinSingleSelect from '../MinSingleSelect';
import DishCard from '../DishCard';
import axiosInstance from '../../../api/axios';
import { BASE_URL } from '../../../constants/apiConstants';
import { useSelector } from 'react-redux';
import { AppDispatch, RootState } from '../../../store/store';
import { SaveExMenuRequisitionRow, getExMenuRequisition } from '../../../services/exmenuprojectionslice';
import { useDispatch } from 'react-redux';
import { toast } from 'react-toastify';
import SingleSelectWithoutClearButton from '../SingleSelectWithoutClearButton';

interface Cafe {
  id: string;
  cafename: string;
  cafecode: string;
}
interface ExceptionalMenuRequisitionTableProps {
  rows: RowData[];
  cafelist: Cafe[];
  daypartcode: any,
  onRowsChange?: (rows: RowData[], daypartcode: any) => void;
}
interface SelectedDish {
  id: string;
  label: string;
  value: string;
  dishcategorycode: string;
}
interface RowData {
  dishid?: string;
  dishcode?: string;
  customdishname?: string;
  portionvariant?: string | number;
  weightperpc?: number | null;
  uomcode?: string;
  uomname?: string;
  requiredfrom?: string;
  dietcategorycode?: string;
  colorcode?: string;
  dishalias?: string;
  priceandunit?: string;
  dishtypecode?: any;
  costcolor?: string;
  isEMR?: boolean;
  exceptionalmenurequisitioncafedetailslist: {
    requisitioncafemappingid?: number;
    exceptionalmenurequisitionid?: string;
    cafecode: string;
    cafename?: string | null;
    pax: number;
    quantity: number;    
  }[];
  remarks?: string;
  exceptionalmenurequisitionid: string;
  dishname?: string;
  weightpiece?: number | null;
}
interface UOMResponse {
  data: {
    data: {
      uomlist: { uomcode: string; name: string }[];
    };
  };
}

interface DishOption {
  label: string;
  id: string;
  value: string;
  dishcategorycode: string;
}

// Normalize a raw stored value or label to the canonical option.value
const getOptionValue = (
  raw?: string | null,
  options?: { id?: string; label: string; value: string }[]
) => {
  if (!raw) return undefined;
  const trimmed = String(raw).trim();
  // exact value match
  let found = options?.find((opt) => opt.value === trimmed);
  if (found) return found.value;
  // case-insensitive value match
  found = options?.find((opt) => opt.value.toLowerCase() === trimmed.toLowerCase());
  if (found) return found.value;
  // label match (case-insensitive)
  found = options?.find((opt) => opt.label.toLowerCase() === trimmed.toLowerCase());
  if (found) return found.value;
  return undefined;
};

const ExceptionalMenuRequisitionTable: React.FC<ExceptionalMenuRequisitionTableProps> = ({
  rows,
  cafelist,
  daypartcode,
  onRowsChange,
}) => {
  
// Safely read `userInfo` from localStorage and normalize `isitemwise` to a boolean.
  let userIsItem = false;
  let isUserType: string | null = null;

  const rawUser = localStorage.getItem('userInfo');

    if (rawUser) {
      const parsed = JSON.parse(rawUser);
      const val = parsed?.userdetails?.isitemwise;    
      userIsItem = val === true;
     const usertype=parsed?.userdetails?.usertype || null;
     if(usertype === "CPU") {
      isUserType ='CPU';
     }else{
      isUserType ='KITCHEN';
     }
    } 
  const REQUIRED_FROM_OPTIONS = !userIsItem?[    
    ...(isUserType === 'CPU' ? [{ id: 'CPU', label: 'CPU', value: 'CPU' }] : []),
    ...(isUserType === 'KITCHEN' ? [{ id: 'KITCHEN', label: 'Kitchen', value: 'KITCHEN' }] : []),
  ]:[    
    { id: 'CK', label: 'CK', value: 'CK' },
    { id: 'CafeKitchen', label: 'Cafe Kitchen', value: 'CafeKitchen' },
  ];
  const dispatch = useDispatch<AppDispatch>();
  const { params, disableRequisition } = useSelector((state: RootState) => state.ExMenuRequisitionParams);
  const { saveLoading, ishybrid } = useSelector((state: RootState) => state.exmenuRequisition);
  const [updatedRows, setUpdatedRows] = useState<RowData[]>([]);
  const [originalValues, setOriginalValues] = useState<Record<string, any>>({});
  const [editRowId, setEditRowId] = useState<string | null>(null);
  const [dishOptions, setDishOptions] = useState<DishOption[]>([]);
  const [UOMOptions, setUOMOptions] = useState<{ id: string; label: string; value: string }[]>([]);
  const userId = localStorage.getItem("userid");
  const [existingDishes, setExistingDishes] = useState<any>([]);
  const onRowsChangeRef = useRef<typeof onRowsChange | null>(null);

  
  useEffect(() => {
    setUpdatedRows(rows);
  }, [rows]);

  useEffect(() => {
    setExistingDishes(
      updatedRows.filter((row) => row.dishcode !== "").map((row) => row.dishcode)
    );
  }, [updatedRows])

  useEffect(() => {
    onRowsChangeRef.current = onRowsChange ?? null;
  }, [onRowsChange]);

  useEffect(() => {
    if (typeof onRowsChangeRef.current === 'function') {
      onRowsChangeRef.current(updatedRows || [], daypartcode);
    }
  }, [updatedRows, daypartcode]);


  const fetchUOMOptions = async () => {
    try {
      const response: UOMResponse = await axiosInstance.post(
        `${BASE_URL}/api/v1/ExceptionalMenuRequisition/GetExceptionalMenuUOMList`
      );
      const uomList = response?.data?.data?.uomlist || [];
      const filteredUOMList = uomList.filter((uom: any) => uom.name !== "Kg");

      return filteredUOMList?.map((uom: any) => ({
        id: uom.uomcode,
        label: uom.name,
        value: uom.uomcode,
      }));
    } catch (error) {
      console.error('Failed to fetch UOM options:', error);
      return [];
    }
  };

  const handleSaveCafe = (exceptionalmenurequisitionid: string, fieldKey?: string) => {
    const row = updatedRows.find(
      (r) => r.exceptionalmenurequisitionid === exceptionalmenurequisitionid
    );
    if (!row) {      
      return;
    }

    // Check if value has actually changed
    if (fieldKey) {
      const originalValue = normalizeValue(originalValues[fieldKey]);
      // For PAX/Quantity fields, use getInputDisplayValue to match what the user sees
      // For UOM fields, get the current value from updatedRows to avoid timing issues
      const isPaxOrQuantityField = fieldKey.includes('-') && 
                                   (fieldKey.endsWith('-pax') || fieldKey.endsWith('-quantity'));
      const isUOMField = fieldKey.includes('-') && fieldKey.endsWith('-uomcode');
      
      let currentValue;
      if (isPaxOrQuantityField) {
        const parts = fieldKey.split('-');
        const field = parts[parts.length - 1]; // 'pax' or 'quantity'
        const cafeCode = parts.slice(1, -1).join('-'); // 'CFC-00022'       
        currentValue = normalizeValue(getInputDisplayValue(row, field, cafeCode));
      } else if (isUOMField) {
        // For UOM fields, get the value directly from the updated row
        currentValue = normalizeValue(row.uomcode || '');
        
      } else {
        currentValue = normalizeValue(getFieldValue(row, fieldKey));
      }
      
      // Enhanced validation to prevent unnecessary API calls
      const hasOriginalValue = originalValues.hasOwnProperty(fieldKey);
      const valuesAreDifferent = originalValue !== currentValue;
      if (!hasOriginalValue) {       
        return;
      }
      
      if (!valuesAreDifferent) {        
        return;
      }
      
      // Additional check: if both values are effectively empty, don't save
      if ((originalValue === '' || originalValue === '0') && (currentValue === '' || currentValue === '0')) {
        const field = fieldKey.split('-').pop();
        if (field === 'pax' || field === 'quantity' || field === 'portionvariant' || field === 'weightpiece') {
            return;
        }
      }
    } else {
      // Fallback to original comparison for non-specific saves
      const originalRow = rows.find(
        (r) => r.exceptionalmenurequisitionid === exceptionalmenurequisitionid
      );
      if (JSON.stringify(row) === JSON.stringify(originalRow)) {        
        return;
      }
    }

    const payload = {
      userid: localStorage.getItem("userid"),
      deliverydate: params.deliverydate,
      sitecode: params.sitecode,
      cafecodelist: params.cafecodelist,
      requesttypeid: params.requesttypeid,
      exceptionalmenurequisitiondaypartlist: [
        {
          daypartcode,
          exceptionalmenurequisitiondeatilslist: [
            {
              exceptionalmenurequisitionid: row.exceptionalmenurequisitionid,
              dishid: row.dishid,
              dishcode: row.dishcode,
              dishname: row.dishname === 'Other' ? row.customdishname : row.dishname,
              portionvariant: row.portionvariant || 0,
              uomcode: row.uomcode || null,
              weightpiece: row.weightpiece || 0,
              remarks: row.remarks || "",
             // requiredfrom: row.requiredfrom || null,
             requiredfrom: !ishybrid ? isUserType : (row.requiredfrom || isUserType || null),
              exceptionalmenurequisitioncafedetailslist: row.exceptionalmenurequisitioncafedetailslist || [],
            }
          ],
        },
      ],
    };

    dispatch(SaveExMenuRequisitionRow(payload));

    // Update original value after successful save
    if (fieldKey) {
      setOriginalValues(prev => ({
        ...prev,
        [fieldKey]: normalizeValue(getFieldValue(row, fieldKey))
      }));
    }
  };

  // Helper function to get field value from row
  const getFieldValue = (row: RowData, fieldKey: string): any => {   
    
    if (fieldKey.includes('-') && fieldKey.split('-').length === 3) {
      // Format: rowId-cafeCode-field
      const [, cafeCode, field] = fieldKey.split('-');      
      
      const cafeDetails = row.exceptionalmenurequisitioncafedetailslist?.find(
        detail => detail.cafecode === cafeCode
      );      
      
      const value = cafeDetails?.[field as keyof typeof cafeDetails];      
      
      // Handle zero values consistently with input display logic
      if (field === 'pax' || field === 'quantity') {
        const result = value !== undefined && value !== null ? value : "";        
        return result;
      }
      return value || '';
    }
    // Format: rowId-field
    const field = fieldKey.split('-').pop();
    const value = (row as any)[field!];
    
    // Handle zero values consistently with input display logic
    if (field === 'portionvariant') {
      return value == 0 ? "" : value;  // Match the input's display logic
    }
    return value || '';
  };

  // Helper function to get the current input display value (what user actually sees)
  const getInputDisplayValue = (row: RowData, field: string, cafeCode?: string): string => {
    if (cafeCode) {
      // Cafe-specific fields (pax, quantity)
      const cafeDetails = row?.exceptionalmenurequisitioncafedetailslist?.find(
        detail => detail.cafecode === cafeCode
      );
      
      if (field === 'pax') {
        return cafeDetails?.pax !== undefined && cafeDetails?.pax !== null ? cafeDetails.pax.toString() : "";
      }
      if (field === 'quantity') {
        return cafeDetails?.quantity !== undefined && cafeDetails?.quantity !== null ? cafeDetails.quantity.toString() : "";
      }
    } else {
      // Simple fields
      if (field === 'portionvariant') {
        return row.portionvariant == 0 ? "" : (row.portionvariant || "").toString();
      }
      if (field === 'weightpiece') {
        return (row.weightpiece || "").toString();
      }
      if (field === 'customdishname') {
        return row.customdishname || "";
      }
      if (field === 'remarks') {
        return row.remarks || "";
      }
    }
    return "";
  };

  // Helper function to normalize values for comparison
  const normalizeValue = (value: any): string => {
    if (value === null || value === undefined) return '';
    if (typeof value === 'number') return value.toString();
    return String(value);
  };

  // Handle input focus to capture original value
  const handleInputFocus = (rowId: string, field: string, currentValue: any, cafeCode?: string) => {
    const fieldKey = cafeCode ? `${rowId}-${cafeCode}-${field}` : `${rowId}-${field}`;
    const normalizedValue = normalizeValue(currentValue);  
    
    setOriginalValues(prev => {
      const updated = {
        ...prev,
        [fieldKey]: normalizedValue
      };      
      return updated;
    });
  };

  // Handle input blur to save if value changed
  const handleInputBlur = async (rowId: string, field: string, cafeCode?: string) => {
    const fieldKey = cafeCode ? `${rowId}-${cafeCode}-${field}` : `${rowId}-${field}`;
    const row = updatedRows.find(r => r.exceptionalmenurequisitionid === rowId);
    
    if (row) {
      // Use the display value (what user actually sees in the input) for accurate comparison
      const currentDisplayValue = normalizeValue(getInputDisplayValue(row, field, cafeCode));
      const originalValue = normalizeValue(originalValues[fieldKey]);
      
      // Additional conditions to ensure API call only on actual value change
      const hasOriginalValue = originalValues.hasOwnProperty(fieldKey);
      const valuesAreDifferent = originalValue !== currentDisplayValue;
      const currentValueExists = currentDisplayValue !== '' && currentDisplayValue !== 'undefined' && currentDisplayValue !== 'null';
            
      // Only call API if:
      // 1. We have captured an original value (user actually focused on the field)
      // 2. The values are actually different
      // 3. The current value is not empty (unless we're clearing a field intentionally)
      if (hasOriginalValue && valuesAreDifferent && (currentValueExists || originalValue !== '')) {      
        handleSaveCafe(rowId, fieldKey);
      } 
    }
  };

  // Special handler for PAX/Quantity blur - these fields change immediately on onChange
  const handlePaxQtyBlur = async (rowId: string, field: string, cafeCode: string) => {
    const fieldKey = `${rowId}-${cafeCode}-${field}`;
    const row = updatedRows.find(r => r.exceptionalmenurequisitionid === rowId);
    
    if (row) {
      const currentDisplayValue = normalizeValue(getInputDisplayValue(row, field, cafeCode));
      const originalValue = normalizeValue(originalValues[fieldKey]);
      const hasOriginalValue = originalValues.hasOwnProperty(fieldKey);
           
      // For PAX/Quantity, trigger API call if:
      // 1. We have original value and it's different from current
      // 2. OR we don't have original value but current value is not empty (new entry)
      const shouldCallAPI = (hasOriginalValue && originalValue !== currentDisplayValue) || 
                           (!hasOriginalValue && currentDisplayValue !== '' && currentDisplayValue !== 'undefined' && currentDisplayValue !== 'null');
      
      if (shouldCallAPI) {        
        handleSaveCafe(rowId, fieldKey);
      } 
    }
  };

  const handlePaxOrQtyChange = (
    rowId: string,
    cafeCode: string,
    field: "pax" | "quantity",
    value: string | number
  ) => {
    if (value === "") {
      setUpdatedRows((prevRows) =>
        prevRows?.map((row) => {
          if (row.exceptionalmenurequisitionid === rowId) {
            let updatedCafeDetails = [...(row.exceptionalmenurequisitioncafedetailslist || [])];
            const existingIndex = updatedCafeDetails.findIndex((detail) => detail.cafecode === cafeCode);

            if (existingIndex !== -1) {
              updatedCafeDetails[existingIndex] = { ...updatedCafeDetails[existingIndex], [field]: 0 };
            }

            return {
              ...row,
              exceptionalmenurequisitioncafedetailslist: updatedCafeDetails,
            };
          }
          return row;
        })
      );
      return;
    }

    const parsedValue = Number(value);
    if (isNaN(parsedValue)) return;

    setUpdatedRows((prevRows) =>
      prevRows?.map((row) => {
        if (row.exceptionalmenurequisitionid === rowId) {
          if (!row.portionvariant || !row.uomcode) return row;

          let updatedPax = 0;
          let updatedQuantity = 0;

          const portionVariantNum = Number(row.portionvariant);

          if (field === "pax") {
            if (row.uomcode === "UOM-00001") {
              updatedQuantity = parsedValue * portionVariantNum;
            } else if (row.uomcode === "UOM-00004") {
              updatedQuantity = (parsedValue * portionVariantNum) / 1000;
            } else if (row.uomcode === "UOM-00003") {
              updatedQuantity = parsedValue * portionVariantNum;
            }
            updatedPax = Math.round(parsedValue);
          }

          if (field === "quantity") {
            if (row.uomcode === "UOM-00001") {
              updatedPax = (parsedValue / portionVariantNum);
            } else if (row.uomcode === "UOM-00004") {
              updatedPax = ((parsedValue * 1000) / portionVariantNum);
            } else if (row.uomcode === "UOM-00003") {
              updatedPax = (parsedValue / portionVariantNum);
            }
            updatedPax = Math.ceil(updatedPax);
            updatedQuantity = parsedValue;
          }

          let updatedCafeDetails = [...(row.exceptionalmenurequisitioncafedetailslist || [])];
          const existingIndex = updatedCafeDetails.findIndex((detail) => detail.cafecode === cafeCode);

          if (existingIndex !== -1) {
            updatedCafeDetails[existingIndex] = {
              ...updatedCafeDetails[existingIndex],
              pax: updatedPax,
              quantity: updatedQuantity,
            };
          } else {
            updatedCafeDetails.push({
              requisitioncafemappingid: 0,
              exceptionalmenurequisitionid: rowId,
              cafecode: cafeCode,
              cafename: null,
              pax: updatedPax,
              quantity: updatedQuantity,
            });
          }

          return {
            ...row,
            exceptionalmenurequisitioncafedetailslist: updatedCafeDetails,
          };
        }
        return row;
      })
    );

    if (!editRowId || editRowId !== rowId) {
      setEditRowId(rowId);
    }
  };


  const handleInputChange = (rowId: string, field: string, value: any) => {
    if (!updatedRows || updatedRows.length === 0) {    
      return;
    }

    const updatedData = updatedRows?.map((row) => {
      if (row.exceptionalmenurequisitionid === rowId) {
        if (field === "portionvariant") {
          const resetCafeDetails = row.exceptionalmenurequisitioncafedetailslist?.map((detail) => ({
            ...detail,
            pax: 0,
            quantity: 0,
          }));

          return {
            ...row,
            portionvariant: value,
            exceptionalmenurequisitioncafedetailslist: resetCafeDetails,
          };
        }
        return {
          ...row,
          [field]: value,
        };
      }
      return row;
    });

    setUpdatedRows(updatedData);
    
    // Clear original values for PAX and quantity fields when portion variant changes (since it resets them)
    if (field === "portionvariant") {
      setOriginalValues(prev => {
        const newOriginalValues = { ...prev };
        Object.keys(newOriginalValues).forEach(key => {
          if (key.startsWith(rowId) && (key.includes('-pax') || key.includes('-quantity'))) {
            delete newOriginalValues[key];            
          }
        });
        return newOriginalValues;
      });
    }
    
    if (!editRowId || editRowId !== rowId) {
      setEditRowId(rowId);
    }
  };

  const handleDish = (rowId: string, selected: string) => {
       
    let selectedDish: SelectedDish | undefined = dishOptions.find((dish) => dish.label === selected);
    if (!selectedDish && selected === "Other") {
      selectedDish = { id: "other", label: "Other", value: "Other", dishcategorycode: "DSH-OTHER" };
    }  

    setUpdatedRows((prevRows) =>
      prevRows?.map((row) => {
        if (row.exceptionalmenurequisitionid === rowId) {          
          return {
            ...row,
            dishname: selected,
            dishid: selectedDish?.id || "",
            dishcode: selectedDish?.dishcategorycode || "",
            customdishname: selected === "Other" ? row.customdishname : "",
          };
        }
        return row;
      })
    );
    
    if (!editRowId || editRowId !== rowId) {
      setEditRowId(rowId);
    }
    
    // Call save API directly for dish selection - bypass validation   
    setTimeout(() => {       
      const row = updatedRows.find(r => r.exceptionalmenurequisitionid === rowId);
      if (!row) {        
        return;
      }
      
      const payload = {
        userid: localStorage.getItem("userid"),
        deliverydate: params.deliverydate,
        sitecode: params.sitecode,
        cafecodelist: params.cafecodelist,
        requesttypeid: params.requesttypeid,
        exceptionalmenurequisitiondaypartlist: [
          {
            daypartcode,
            exceptionalmenurequisitiondeatilslist: [
              {
                exceptionalmenurequisitionid: rowId,
                dishid: selectedDish?.id || "",
                dishcode: selectedDish?.dishcategorycode || "",
                dishname: selected === "Other" ? (row.customdishname ? row.customdishname.trim() : row.customdishname) : selected,
                portionvariant: row.portionvariant || 0,
                uomcode: row.uomcode || null,
                weightpiece: row.weightpiece || 0,
                remarks: row.remarks || "",
                requiredfrom: !ishybrid ? isUserType : (row.requiredfrom || isUserType || null),
                exceptionalmenurequisitioncafedetailslist: row.exceptionalmenurequisitioncafedetailslist || [],
              }
            ],
          },
        ],
      };     
     
      dispatch(SaveExMenuRequisitionRow(payload));
    }, 100);
  };


  const handleUOMChange = (rowId: string, selectedUOM: string) => {
    const row = updatedRows.find(r => r.exceptionalmenurequisitionid === rowId);
    if (!row) {     
      return;
    }
    
    const currentUOMValue = row.uomcode || '';
    const fieldKey = `${rowId}-uomcode`;
   if (currentUOMValue !== selectedUOM && selectedUOM !== '' && selectedUOM !== null && selectedUOM !== undefined) {
    // Set original value before updating state
    setOriginalValues(prev => {
        const newOriginalValues = { 
          ...prev,
          [fieldKey]: currentUOMValue
        };       
        Object.keys(newOriginalValues).forEach(key => {
          if (key.startsWith(rowId) && (key.includes('-pax') || key.includes('-quantity'))) {
            delete newOriginalValues[key];            
          }
        });
        return newOriginalValues;
      });
      
      // Update the rows state
      setUpdatedRows((prevRows) =>
        prevRows?.map((row) =>
          row.exceptionalmenurequisitionid === rowId
            ? {
              ...row,
              uomcode: selectedUOM,
              exceptionalmenurequisitioncafedetailslist: row.exceptionalmenurequisitioncafedetailslist?.map(detail => ({
                ...detail,
                pax: 0,
                quantity: 0,
              })),
            }
            : row
        )
      );
      
      if (!editRowId || editRowId !== rowId) {
        setEditRowId(rowId);
      }
      
      // Call save API immediately when UOM is changed - bypass all validation   
      const payload = {
        userid: localStorage.getItem("userid"),
        deliverydate: params.deliverydate,
        sitecode: params.sitecode,
        cafecodelist: params.cafecodelist,
        requesttypeid: params.requesttypeid,
        exceptionalmenurequisitiondaypartlist: [
          {
            daypartcode,
            exceptionalmenurequisitiondeatilslist: [
              {
                exceptionalmenurequisitionid: rowId,
                dishid: row.dishid,
                dishcode: row.dishcode,
                dishname: row.dishname === 'Other' ? row.customdishname : row.dishname,
                portionvariant: row.portionvariant || 0,
                uomcode: selectedUOM,
                weightpiece: row.weightpiece || 0,
                remarks: row.remarks || "",
                requiredfrom: !ishybrid ? isUserType : (row.requiredfrom || isUserType || null),
                exceptionalmenurequisitioncafedetailslist: row.exceptionalmenurequisitioncafedetailslist?.map(detail => ({
                  ...detail,
                  pax: 0,
                  quantity: 0,
                })) || [],
              }
            ],
          },
        ],
      };
      dispatch(SaveExMenuRequisitionRow(payload));
      
      // Update original value after API call
      setOriginalValues(prev => ({
        ...prev,
        [fieldKey]: selectedUOM
      }));
    } 
  };

  const handleRequiredFromChange = (rowId: string, selectedValue: string) => {
    // Normalize selected value to the canonical option.value then store
    const canonical = getOptionValue(selectedValue, REQUIRED_FROM_OPTIONS) || selectedValue;

    // Update local state and immediately dispatch a save with the updated row so the API receives requiredfrom
    setUpdatedRows((prevRows) => {
      const newRows = prevRows?.map((row) =>
        row.exceptionalmenurequisitionid === rowId ? { ...row, requiredfrom: canonical } : row
      );

      // Build payload for the updated row and dispatch immediately
      const rowToSave = newRows?.find((r) => r.exceptionalmenurequisitionid === rowId);
      if (rowToSave) {
        const payload = {
          userid: localStorage.getItem("userid"),
          deliverydate: params.deliverydate,
          sitecode: params.sitecode,
          cafecodelist: params.cafecodelist,
          requesttypeid: params.requesttypeid,
          exceptionalmenurequisitiondaypartlist: [
            {
              daypartcode,
              exceptionalmenurequisitiondeatilslist: [
                {
                  exceptionalmenurequisitionid: rowToSave.exceptionalmenurequisitionid,
                  dishid: rowToSave.dishid,
                  dishcode: rowToSave.dishcode,
                  dishname: rowToSave.dishname === 'Other' ? rowToSave.customdishname : rowToSave.dishname,
                  portionvariant: rowToSave.portionvariant || 0,
                  uomcode: rowToSave.uomcode || null,
                  weightpiece: rowToSave.weightpiece || 0,
                  remarks: rowToSave.remarks || "",
                  requiredfrom: rowToSave.requiredfrom || null,
                  exceptionalmenurequisitioncafedetailslist: rowToSave.exceptionalmenurequisitioncafedetailslist || [],
                }
              ],
            },
          ],
        };

        dispatch(SaveExMenuRequisitionRow(payload));
      }

      return newRows;
    });

    // Ensure editRowId is set so debounced save runs (and UI stays in edit mode)
    if (!editRowId || editRowId !== rowId) {
      setEditRowId(rowId);
    }
  };

  const fetchDishOptions = async () => {
    try {
      const payload = {
        sitelistid: [params.sitecode],
        userid: userId,
      };

      const response = await axiosInstance.post(
        `${BASE_URL}/api/v1/Dish/GetDish`,
        payload
      );

      const dishes: any[] = (response as any)?.data?.data?.dishes || [];
      const topDishes = dishes?.map((dish: any) => ({
        id: dish.dishid,
        label: dish.dishname,
        value: dish.dishname,
        dishcategorycode: dish.dishid
      }));

      return [
        ...topDishes,
        {
          id: "other",
          label: "Other",
          value: "Other",
          dishcategorycode: "DSH-OTHER"
        },
      ];
    } catch (error) {
      console.error("Failed to fetch dishes:", error);
      return [
        {
          id: "other",
          label: "Other",
          value: "Other",
          dishcategorycode: "DSH-OTHER"
        },
      ];
    }
  };


  useEffect(() => {
    const loadUOMOptions = async () => {
      const options = await fetchUOMOptions();
      setUOMOptions(options);
      const fetchedDishes = await fetchDishOptions();
      setDishOptions(fetchedDishes);
    };
    loadUOMOptions();
  }, []);


  const handleEditClick = async (rowId: string) => {
    setEditRowId(rowId);
  };

  const handleDeleteRow = async (id: string) => {
    const payload = {
      exceptionalmenurequisitionid: id,
      userid: localStorage.getItem("userid"),
      deliverydate: params.deliverydate,
      sitecode: params.sitecode,
      cafecodelist: params.cafecodelist,
      requesttypeid: params.requesttypeid,
      daypartcode
    };

    try {
      await axiosInstance.post(
        `${BASE_URL}/api/v1/ExceptionalMenuRequisition/DeleteExceptionalMenuRequisition`,
        payload
      );
      // Optimistically update local table for immediate feedback
      setUpdatedRows((prevRows) => prevRows.filter(row => row.exceptionalmenurequisitionid !== id));
      toast("Dish deleted successfully!", { type: "success" });

      // Refresh authoritative data in redux so page-level validation (which
      // reads state.exmenuRequisition) reflects the deletion immediately.
      try {
        const getpayload = {
          deliverydate: params.deliverydate,
          sitecode: params.sitecode,
          cafecodelist: params.cafecodelist,
          requesttypeid: params.requesttypeid,
          userid: userId,
          searchtext: params?.searchtext || "",
        };
        dispatch(getExMenuRequisition(getpayload));
      } catch (e) {
        // If refresh fails, we still removed the row locally; log for debugging.
        console.error('Failed to refresh Exceptional Menu Requisition after delete', e);
      }
    } catch (error: any) {
      if (error.response.data.responsecode === 501) {
        toast("Please select all existing cafes to delete.", { type: "error" });
      }
      console.error("Error deleting row:", error);
    }
  };


  return (
    <div className="table-wrapper exceptional-menu-requisition-table">
      <TableContainer component={Paper} className="table-inner" sx={{ maxHeight: 440 }}>
        <MuiTable stickyHeader>
          <TableHead>
            <TableRow>
              <TableCell rowSpan={2}>
                <Typography variant="h6" className="table-title">
                  Dish Name
                </Typography>
              </TableCell>
              <TableCell rowSpan={2}>
                <Typography variant="h6" className="table-title">
                  Dish Name (Custom)
                </Typography>
              </TableCell>
               {!ishybrid ? false : (
              <TableCell rowSpan={2}>
                <Typography variant="h6" className="table-title center">
                  Required From
                </Typography>
              </TableCell>
              )}
              <TableCell rowSpan={2}>
                <Typography variant="h6" className="table-title center">
                  Portion Variant
                </Typography>
              </TableCell>
              <TableCell rowSpan={2}>
                <Typography variant="h6" className="table-title center">
                  UOM
                </Typography>
              </TableCell>
              <TableCell rowSpan={2}>
                <Typography variant="h6" className="table-title center">
                  Wt/Pc (gms)
                </Typography>
              </TableCell>
              {cafelist?.map((cafe) => (
                <TableCell colSpan={2} key={cafe.cafecode}>
                  <Typography variant="h6" className="table-title center">
                    {cafe.cafename}
                  </Typography>
                </TableCell>
              ))}
              <TableCell rowSpan={2}>
                <Typography variant="h6" className="table-title">
                  Remarks
                </Typography>
              </TableCell>
              <TableCell rowSpan={2}>
                <Typography variant="h6" className="table-title center">
                  Actions
                </Typography>
              </TableCell>
            </TableRow>
            <TableRow>
              {cafelist?.map((cafe) => (
                <>
                  <TableCell key={`${cafe.cafecode}-pax`} className="subtask-title">
                    <Typography variant="h6" className="table-title">Pax</Typography>
                  </TableCell>
                  <TableCell key={`${cafe.cafecode}-quantity`} className="subtask-title">
                    <Typography variant="h6" className="table-title">Req. Qty (Kg/Pcs)</Typography>
                  </TableCell>
                </>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {updatedRows?.map((row) => (
              <TableRow key={row.exceptionalmenurequisitionid}>
                <TableCell>
                  {row?.dishname?.trim().toLowerCase() === '' ? (
                    <SingleSelectWithoutClearButton
                      options={dishOptions?.filter((dish) => !existingDishes.includes(dish.id)).map((option) => option.label.toString())}
                      selectedValues={[row?.dishname || ""]}
                      onApply={(event) => {                        
                        if (event) {
                          const selectedValue = event;
                         handleDish(row.exceptionalmenurequisitionid, String(selectedValue));
                        }
                      }}
                      placeholder="Select Dish"
                    />
                  ) : row?.dishname?.trim().toLowerCase() === 'other' ? (
                    <div>Other</div>) :
                    (
                      <DishCard
                        title={row.dishname}
                        portionuomcode={row.uomname || null}
                        typecode={row?.dishtypecode || null}
                        dietcode={row?.dietcategorycode}
                        colorcode={row?.colorcode}
                        alias={row?.dishalias}
                        priceandunit={row?.priceandunit}
                        IsItems={true}
                        isexmenureq={true}
                        costcolor={row?.costcolor}
                        isEMR={true}
                      />
                    )}
                </TableCell>
                <TableCell className="subtask-title">
                  <input
                    type="text"
                    placeholder="Custom Dish Name"
                    value={row?.dishname?.trim().toLowerCase() === 'other' ? row.customdishname || "" : ""}
                    onChange={(e) =>
                      handleInputChange(row.exceptionalmenurequisitionid, "customdishname", e.target.value)
                    }
                    onFocus={(e) =>
                      handleInputFocus(row.exceptionalmenurequisitionid, "customdishname", e.target.value)
                    }
                    onBlur={() =>
                      handleInputBlur(row.exceptionalmenurequisitionid, "customdishname")
                    }
                    readOnly={saveLoading}
                    disabled={editRowId !== row.exceptionalmenurequisitionid || disableRequisition || (row.dishname !== "Other")}
                    autoFocus={editRowId === row.exceptionalmenurequisitionid}
                  />
                </TableCell>
                {!ishybrid ? false : (
                <TableCell className="subtask-title">
                  <MinSingleSelect
                    options={REQUIRED_FROM_OPTIONS}
                    value={getOptionValue(row.requiredfrom || isUserType, REQUIRED_FROM_OPTIONS) || ''}
                    onChange={(event) => handleRequiredFromChange(row.exceptionalmenurequisitionid, event.target.value as string)}
                    placeholder="Select"
                    disabled={!userIsItem || !(editRowId === row.exceptionalmenurequisitionid) || disableRequisition}
                  />
                </TableCell>
                )}
                <TableCell className="subtask-title grey-bg">
                  <input type="number" placeholder="0" className="center"
                    value={row.portionvariant && Number(row.portionvariant) > 0 ? row.portionvariant : ""}
                    onChange={(e) =>
                      handleInputChange(row.exceptionalmenurequisitionid, "portionvariant", e.target.value)
                    }
                      onKeyDown={(e) => {
                              // Prevent 'e', 'E', '+', '-' from being entered in number field
                              if (['e', 'E', '+', '-'].includes(e.key)) {
                                e.preventDefault();
                              }
                            }}
                    onFocus={(e) => {
                      // Store original value for comparison when field is focused
                      const storedValue = row.portionvariant || 0;
                      handleInputFocus(row.exceptionalmenurequisitionid, "portionvariant", storedValue);
                    }}
                    onBlur={() =>
                      handleInputBlur(row.exceptionalmenurequisitionid, "portionvariant")
                    }
                    readOnly={saveLoading}
                    disabled={!(editRowId === row.exceptionalmenurequisitionid) || disableRequisition}
                    autoFocus={editRowId === row.exceptionalmenurequisitionid}
                  />

                </TableCell>
                <TableCell className="subtask-title grey-bg">
                  <MinSingleSelect
                    options={UOMOptions}
                    value={row.uomcode || ''}
                    onChange={(event) => handleUOMChange(row.exceptionalmenurequisitionid, event.target.value as string)}
                    placeholder="Select"
                    disabled={!(editRowId === row.exceptionalmenurequisitionid) || disableRequisition}
                  />


                </TableCell>
                <TableCell className='center'>
                  {row.uomcode === 'UOM-00003' ? (
                    <input
                      type="number"
                      placeholder=""
                      className="center"
                      value={row.weightpiece || ""}
                      onChange={(e) =>
                        handleInputChange(row.exceptionalmenurequisitionid, "weightpiece", e.target.value)
                      }
                        onKeyDown={(e) => {
                              // Prevent 'e', 'E', '+', '-' from being entered in number field
                              if (['e', 'E', '+', '-'].includes(e.key)) {
                                e.preventDefault();
                              }
                            }}
                      onFocus={(e) =>
                        handleInputFocus(row.exceptionalmenurequisitionid, "weightpiece", e.target.value)
                      }
                      onBlur={() =>
                        handleInputBlur(row.exceptionalmenurequisitionid, "weightpiece")
                      }
                      readOnly={saveLoading}
                      disabled={!(editRowId === row.exceptionalmenurequisitionid) || !row.uomcode || disableRequisition}
                      autoFocus={editRowId === row.exceptionalmenurequisitionid}
                    />
                  ) : (
                    '-'
                  )}
                </TableCell>


                {cafelist?.map((cafe, index) => {
                  const cafeDetails = row?.exceptionalmenurequisitioncafedetailslist?.find(
                    (detail) => detail.cafecode === cafe.cafecode
                  );
                  return (
                    <>
                      <TableCell key={`${index}-${cafe.cafecode}-pax`} className="subtask-title grey-bg">
                        <input
                          type="number"
                          placeholder="0"
                          className="center"
                          value={cafeDetails?.pax && cafeDetails.pax > 0 ? cafeDetails.pax : ""}
                          onChange={(e) => {
                            const input = e.target.value;
                            if (/^\d*$/.test(input)) {
                              handlePaxOrQtyChange(row.exceptionalmenurequisitionid, cafe.cafecode, "pax", input ? input : 0)
                            }
                          }}
                            onKeyDown={(e) => {
                              // Prevent 'e', 'E', '+', '-' from being entered in number field
                              if (['e', 'E', '+', '-'].includes(e.key)) {
                                e.preventDefault();
                              }
                            }}
                          onFocus={(e) => {
                            // Store original value for comparison when field is focused
                            const storedValue = cafeDetails?.pax || 0;
                            handleInputFocus(row.exceptionalmenurequisitionid, "pax", storedValue, cafe.cafecode);
                          }}
                          onBlur={() =>
                            handlePaxQtyBlur(row.exceptionalmenurequisitionid, "pax", cafe.cafecode)
                          }
                          readOnly={saveLoading}
                          disabled={editRowId !== row.exceptionalmenurequisitionid || !row.uomcode || disableRequisition || !row.portionvariant}
                          autoFocus={editRowId === row.exceptionalmenurequisitionid}
                        />
                      </TableCell>
                      <TableCell key={`${index}-${cafe.cafecode}-quantity`} className="subtask-title grey-bg qty-input">
                        <input
                          type="number"
                          placeholder="0"
                          className="center"
                          value={cafeDetails?.quantity && cafeDetails.quantity > 0 ? cafeDetails.quantity : ""}
                          onChange={(e) => {
                            const input = e.target.value;
                            if (
                              (row.uomcode === "UOM-00003") && /^\d*$/.test(input) ||
                              (row.uomcode !== "UOM-00003" && /^\d*\.?\d{0,3}$/.test(input))
                            ) {
                              handlePaxOrQtyChange(row.exceptionalmenurequisitionid, cafe.cafecode, "quantity", input ? input : 0);
                            }
                          }}
                            onKeyDown={(e) => {
                              // Prevent 'e', 'E', '+', '-' from being entered in number field
                              if (['e', 'E', '+', '-'].includes(e.key)) {
                                e.preventDefault();
                              }
                            }}
                          onFocus={(e) => {
                            // Store original value for comparison when field is focused
                            const storedValue = cafeDetails?.quantity || 0;
                            handleInputFocus(row.exceptionalmenurequisitionid, "quantity", storedValue, cafe.cafecode);
                          }}
                          onBlur={() =>
                            handlePaxQtyBlur(row.exceptionalmenurequisitionid, "quantity", cafe.cafecode)
                          }
                          readOnly={saveLoading}
                          disabled={editRowId !== row.exceptionalmenurequisitionid || !row.uomcode || disableRequisition || !row.portionvariant}
                          autoFocus={editRowId === row.exceptionalmenurequisitionid}
                        />
                        <span key={`${row.exceptionalmenurequisitionid}-${row.uomcode}-label`}>
                          {/* {row.uomcode ? (UOMOptions.find(uom => uom.value === row.uomcode)?.label || row.uomcode) : ""} */}
                         {row.uomcode ? (() => {
                            const uomLabel = UOMOptions.find(uom => uom.value === row.uomcode)?.label;
                            return uomLabel === "gms" ? "Kg" : (uomLabel || row.uomcode);
                          })() : ""}
                        </span>

                      </TableCell>
                    </>
                  );
                })}
                <TableCell className="subtask-title">
                  <input type="text" value={row.remarks || ""} onChange={(e) =>
                    handleInputChange(row.exceptionalmenurequisitionid, "remarks", e.target.value)
                  } 
                  onFocus={(e) =>
                    handleInputFocus(row.exceptionalmenurequisitionid, "remarks", e.target.value)
                  }
                  onBlur={() =>
                    handleInputBlur(row.exceptionalmenurequisitionid, "remarks")
                  }
                  disabled={editRowId !== row.exceptionalmenurequisitionid || disableRequisition}
                  autoFocus={editRowId === row.exceptionalmenurequisitionid}
                  />
                </TableCell>
                <TableCell className="subtask-title center">
                  <div className="flex justify-content-center align-center btn-wrap">
                    <button disabled={!row.dishname || disableRequisition} onClick={() => handleEditClick(row.exceptionalmenurequisitionid)}>
                      <IconEdit />
                    </button>
                    <button disabled={disableRequisition} onClick={() => handleDeleteRow(row.exceptionalmenurequisitionid)}>
                      <IconDelete />
                    </button>
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </MuiTable>
      </TableContainer>
    </div>
  );
};
export default ExceptionalMenuRequisitionTable;